import React, { useState, useEffect } from 'react';
import { db } from '../../firebase'; // Adjust path to your firebase config
import { collection, query, orderBy, onSnapshot, addDoc, deleteDoc, doc } from 'firebase/firestore';
import { FaTrash, FaPlus, FaUpload } from 'react-icons/fa';

function BannersParty() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Reference to the 'banners' collection
  const bannersCollectionRef = collection(db, 'banners Party');

  useEffect(() => {
    const q = query(bannersCollectionRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const bannersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setBanners(bannersList);
      setLoading(false);
    }, (err) => {
      console.error("Error fetching banners: ", err);
      setError('Failed to load banners.');
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) {
      alert("Please select a file.");
      return;
    }

    setUploading(true);

    // FileReader is a browser API to read file content
    const reader = new FileReader();
    reader.readAsDataURL(selectedFile); // This converts the image to a Base64 string

    // This function runs when the conversion is complete
    reader.onloadend = async () => {
      const base64String = reader.result;

      // *** CRITICAL CHECK: Enforce Firestore document size limit ***
      if (base64String.length > 1048576) {
        alert("Error: Image is too large to save in Firestore. Please use smaller images (under ~700KB).");
        setUploading(false);
        return;
      }

      try {
        // Add a new document to the 'banners' collection with the Base64 data
        await addDoc(bannersCollectionRef, {
          imageUrl: base64String, // The Base64 data string
          createdAt: new Date(),
          fileName: selectedFile.name // Optional: store the original file name
        });
        alert("Banner saved successfully!");
      } catch (err) {
        console.error("Error saving to Firestore: ", err);
        alert("Error saving banner data.");
      } finally {
        setUploading(false);
        setSelectedFile(null);
        document.getElementById('file-input').value = '';
      }
    };

    reader.onerror = () => {
      console.error("Error reading file.");
      alert("Could not read the selected file.");
      setUploading(false);
    };
  };

  const handleDelete = async (banner) => {
    if (window.confirm(`Are you sure you want to delete this banner?`)) {
      try {
        // Deleting is much simpler now - just remove the document.
        const bannerDocRef = doc(db, 'banners', banner.id);
        await deleteDoc(bannerDocRef);
        alert('Banner deleted successfully!');
      } catch (err) {
        console.error("Error deleting banner: ", err);
        alert('Failed to delete banner.');
      }
    }
  };

  if (loading) return <div style={styles.centered}>Loading Banners...</div>;
  if (error) return <div style={{ ...styles.centered, color: 'red' }}>{error}</div>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2>Banner Management (Base64 Method)</h2>
        <div style={styles.uploadContainer}>
          <input
            id="file-input"
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ marginRight: '10px' }}
            disabled={uploading}
          />
          <button style={styles.addButton} onClick={handleUpload} disabled={uploading || !selectedFile}>
            {uploading ? <FaUpload style={{ marginRight: '8px' }} /> : <FaPlus style={{ marginRight: '8px' }} />}
            {uploading ? 'Processing...' : 'Add Banner'}
          </button>
        </div>
      </div>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Image</th>
            <th style={styles.th}>File Name</th>
            <th style={styles.th}>Created At</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {banners.length > 0 ? (
            banners.map(banner => (
              <tr key={banner.id}>
                <td style={styles.td}>
                  {/* The src attribute can directly use the Base64 string */}
                  <img src={banner.imageUrl} alt={banner.fileName} style={styles.image} />
                </td>
                <td style={styles.td}>{banner.fileName}</td>
                <td style={styles.td}>{new Date(banner.createdAt.seconds * 1000).toLocaleString()}</td>
                <td style={styles.td}>
                  <button
                    style={{...styles.actionButton, ...styles.deleteButton}}
                    onClick={() => handleDelete(banner)}
                    disabled={uploading}
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="4" style={styles.centered}>No banners found. Add one to get started.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// Re-using the same styles
const styles = {
  container: { padding: '2rem', maxWidth: '1000px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1rem' },
  uploadContainer: { display: 'flex', alignItems: 'center' },
  addButton: { display: 'flex', alignItems: 'center', padding: '10px 15px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px' },
  table: { width: '100%', borderCollapse: 'collapse', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  th: { backgroundColor: '#f8f9fa', padding: '12px', border: '1px solid #ddd', textAlign: 'left' },
  td: { padding: '12px', border: '1px solid #ddd', verticalAlign: 'middle', wordBreak: 'break-word' },
  image: { width: '100px', height: '50px', objectFit: 'cover', borderRadius: '4px' },
  actionButton: { border: 'none', padding: '8px 12px', borderRadius: '5px', cursor: 'pointer', color: 'white' },
  deleteButton: { backgroundColor: '#dc3545' },
  centered: { textAlign: 'center', padding: '20px', fontSize: '18px' }
};

export default BannersParty;